import { canRegisterInAnotherCategory } from "../../shared/utils/registrationCategories";
import { useEffect, useMemo, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import EventLocationActions from "../../../../shared/components/EventLocationActions";
import LoadingPage from "../../../../shared/components/LoadingPage";
import Spinner from "../../../../shared/components/Spinner";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import {
  formatDateTimeLabel,
  formatEventDateRangeLabel,
  parseDateTimeValue,
} from "../../../../shared/utils/dateTime";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import usePublicOrganizerVisualIdentityTheme from "../../../auth/shared/hooks/usePublicOrganizerVisualIdentityTheme";
import { buildLoginHref } from "../../../../shared/lib/loginRedirect";
import { getChallengeByIdMaybeAuth } from "../../../challenges/services/challengeAdminService";
import { ChallengeDetail } from "../../../challenges/types/challengeAdmin";
import { resolveChallengeCoverImage } from "../../../challenges/utils/challengeCoverImage";
import {
  getPocoTotalPlayersCapacity,
  getPocoTotalRemainingPlayers,
} from "../../../challenges/utils/pocoSchedule";
import { useSiteContent } from "../../../home/shared/hooks/useSiteContent";
import {
  EventChallengeLink,
  EventRegistration,
  getEventChallengeLink,
  getMyEventRegistration,
} from "../../shared/services/eventRegistrationService";
import "./EventDetailsPage.css";

type EventDetailsPageProps = {
  eventIndex?: number;
  eventId?: string;
};

function categoryLabel(code?: string | null) {
  if (!code) return siteTexts.eventDetails.categories.toBeDefined;
  if (code === "7a") return "Iniciante (7ª)";
  if (code === "6a") return "6ª";
  if (code === "5a") return "5ª";
  if (code === "4a") return "4ª";
  if (code === "3a") return "3ª";
  if (code === "2a") return "2ª";
  if (code === "open") return "Open";
  if (code === "free") return siteTexts.common.category.free;
  return String(code || "").toUpperCase();
}

function challengeTypeLabel(type?: string | null) {
  const labels = siteTexts.eventDetails.challengeTypes;
  if (type === "super8") return labels.super8;
  if (type === "poco") return labels.poco;
  if (type === "tournament") return labels.tournament;
  if (type === "challenge") return labels.challenge;
  return labels.challenge;
}

function challengeCategorySummary(input?: {
  categoryGender?: string | null;
  category?: string | null;
  categoryMale?: string | null;
  categoryFemale?: string | null;
}) {
  const gender = String(input?.categoryGender || "").trim().toLowerCase();
  if (gender === "mixed" || gender === "open") {
    const genderLabel =
      gender === "open"
        ? siteTexts.common.challengeGender.open
        : siteTexts.common.challengeGender.mixed;
    return `${genderLabel} · ${siteTexts.common.challengeGender.maleShort} ${categoryLabel(input?.categoryMale)} / ${siteTexts.common.challengeGender.femaleShort} ${categoryLabel(input?.categoryFemale)}`;
  }
  if (gender === "male") {
    return `${siteTexts.common.challengeGender.male} · ${categoryLabel(input?.category)}`;
  }
  if (gender === "female") {
    return `${siteTexts.common.challengeGender.female} · ${categoryLabel(input?.category)}`;
  }
  return categoryLabel(input?.category);
}

function minimumParticipantsByType(challenge?: ChallengeDetail | null) {
  if (challenge?.challengeType === "super8") return 8;
  if (challenge?.challengeType === "poco") {
    return getPocoTotalPlayersCapacity({
      courtsCount: challenge.courtsCount,
      playersCount: challenge.playersCount,
      registrationCategories: challenge.registrationCategories,
    });
  }
  return 12;
}

function registrationStatusLabel(status?: string | null) {
  const labels = siteTexts.eventDetails.registrationStatus;
  if (status === "pending") return labels.pending;
  if (status === "confirmed") return labels.confirmed;
  if (status === "waitlist") return labels.waitlist;
  if (status === "canceled") return labels.canceled;
  return labels.none;
}

function paymentStatusLabel(status?: string | null) {
  const labels = siteTexts.eventDetails.paymentStatus;
  if (status === "pending") return labels.pending;
  if (status === "confirmed") return labels.confirmed;
  if (status === "canceled") return labels.canceled;
  if (status === "refunded") return labels.refunded;
  return labels.toBeDefined;
}

function challengeStatusLabel(status?: string | null) {
  const labels = siteTexts.eventDetails.challengeStatus;
  if (status === "pending") return labels.pending;
  if (status === "started") return labels.started;
  if (status === "finished") return labels.finished;
  if (status === "canceled") return labels.canceled;
  return labels.event;
}

function challengeStatusTone(status?: string | null) {
  if (status === "pending") return "pending";
  if (status === "started") return "started";
  if (status === "finished") return "finished";
  if (status === "canceled") return "canceled";
  return "pending";
}

function registrationStatusTone(status?: string | null, paymentStatus?: string | null) {
  if (status === "confirmed" && paymentStatus === "confirmed") return "confirmed";
  if (status === "waitlist") return "waitlist";
  if (status === "canceled" || paymentStatus === "canceled") return "canceled";
  return "pending";
}

function formatCurrency(cents: number | null | undefined) {
  if (!Number.isFinite(Number(cents))) {
    return siteTexts.eventDetails.paymentStatus.toBeDefined;
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(cents) / 100);
}

function getRegistrationConfirmationDate(
  registration: NonNullable<ChallengeDetail["registrations"]>[number],
) {
  if (registration.paymentStatus === "confirmed") {
    return registration.paymentConfirmedAt || null;
  }
  return null;
}

function getRegistrationOrderingDate(
  registration: NonNullable<ChallengeDetail["registrations"]>[number],
) {
  if (registration.paymentStatus === "confirmed") {
    return (
      registration.paymentConfirmedAt ||
      registration.updatedAt ||
      registration.createdAt ||
      null
    );
  }
  return registration.updatedAt || registration.createdAt || null;
}

function getRegistrationSortTime(value?: string | null) {
  const parsed = parseDateTimeValue(value);
  return parsed ? parsed.getTime() : Number.POSITIVE_INFINITY;
}

export default function EventDetailsPage({
  eventIndex,
  eventId,
}: EventDetailsPageProps) {
  const { content } = useSiteContent();
  const { token } = useAuth();
  const resolvedEventIndex = useMemo(() => {
    if (Number.isFinite(eventIndex) && Number(eventIndex) >= 0) return Number(eventIndex);
    return -1;
  }, [eventIndex]);
  const resolvedEventId = useMemo(() => {
    const byProp = String(eventId || "").trim();
    if (byProp) return byProp;
    if (resolvedEventIndex >= 0) return `evt-${resolvedEventIndex}`;
    return "";
  }, [eventId, resolvedEventIndex]);
  const registrationHref = useMemo(() => {
    const target = resolvedEventId
      ? `#/eventos/id/${encodeURIComponent(resolvedEventId)}/inscricao`
      : resolvedEventIndex >= 0
        ? `#/eventos/${resolvedEventIndex}/inscricao`
        : "#/eventos";
    return token ? target : buildLoginHref(target);
  }, [resolvedEventId, resolvedEventIndex, token]);

  const [challengeLink, setChallengeLink] = useState<EventChallengeLink | null>(
    null,
  );
  const [challengeLinkLoading, setChallengeLinkLoading] = useState(
    Boolean(resolvedEventId),
  );
  const [myRegistration, setMyRegistration] = useState<EventRegistration | null>(
    null,
  );
  const [myRegistrationLoading, setMyRegistrationLoading] = useState(false);
  const [challengeDetail, setChallengeDetail] = useState<ChallengeDetail | null>(
    null,
  );
  const [challengeDetailLoading, setChallengeDetailLoading] = useState(false);
  usePublicOrganizerVisualIdentityTheme(
    challengeDetail?.organizerUserId,
    challengeDetail?.isOfficial,
  );

  useEffect(() => {
    if (!resolvedEventId) return;
    let active = true;
    setChallengeLinkLoading(true);
    getEventChallengeLink(resolvedEventId)
      .then((data) => {
        if (!active) return;
        setChallengeLink(data);
      })
      .catch(() => {
        if (!active) return;
        setChallengeLink(null);
      })
      .finally(() => {
        if (active) setChallengeLinkLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedEventId]);

  useEffect(() => {
    if (!token || !resolvedEventId) {
      setMyRegistration(null);
      return;
    }
    let active = true;
    setMyRegistrationLoading(true);
    getMyEventRegistration(token, resolvedEventId)
      .then((data) => {
        if (!active) return;
        setMyRegistration(data);
      })
      .catch(() => {
        if (!active) return;
        setMyRegistration(null);
      })
      .finally(() => {
        if (active) setMyRegistrationLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedEventId, token]);

  useEffect(() => {
    const tournamentId = Number(challengeLink?.tournamentId || 0);
    if (!Number.isFinite(tournamentId) || tournamentId <= 0) {
      setChallengeDetail(null);
      return;
    }
    let active = true;
    setChallengeDetailLoading(true);
    getChallengeByIdMaybeAuth(tournamentId, token || undefined)
      .then((data) => {
        if (!active) return;
        setChallengeDetail(data);
      })
      .catch(() => {
        if (!active) return;
        setChallengeDetail(null);
      })
      .finally(() => {
        if (active) setChallengeDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [challengeLink?.tournamentId, token]);

  const event = useMemo(() => {
    if (!challengeLink && !challengeDetail) return null;
    return {
      title:
        String(challengeLink?.challengeName || "").trim() ||
        String(challengeDetail?.challengeName || challengeDetail?.groupName || "").trim(),
      date: formatEventDateRangeLabel(
        challengeLink?.scheduledAt || challengeDetail?.scheduledAt || null,
        challengeLink?.scheduledEndAt || challengeDetail?.scheduledEndAt || null,
      ),
      address:
        String(challengeLink?.address || "").trim() ||
        String(challengeDetail?.address || "").trim(),
      city:
        String(challengeLink?.city || "").trim() ||
        String(challengeDetail?.city || "").trim(),
      state:
        String(challengeLink?.state || "").trim() ||
        String(challengeDetail?.state || "").trim(),
      description: String(challengeDetail?.description || "").trim(),
      details: String(challengeDetail?.description || "").trim(),
      imageUrl: resolveChallengeCoverImage(challengeDetail, content.events),
      registrationType: challengeLink?.registrationType || challengeDetail?.registrationType,
      amountPerPlayerCents: challengeLink?.amountPerPlayerCents ?? null,
    };
  }, [challengeDetail, challengeLink, content.events]);

  if (challengeLinkLoading || challengeDetailLoading) {
    return <LoadingPage label={siteTexts.eventDetails.loading} />;
  }

  if (!event) {
    return (
      <AppLayout title={siteTexts.eventDetails.notFoundTitle}>
        <div className="empty-state">
          <p>{siteTexts.eventDetails.notFoundMessage}</p>
        </div>
      </AppLayout>
    );
  }

  const hasChallengeLink = Boolean(challengeLink);
  const hasRegistration =
    Boolean(myRegistration) && myRegistration?.status !== "canceled";
  const registrationsOpen = Boolean(challengeLink?.registrationsOpen);
  const canRegisterNow = hasRegistration || registrationsOpen;
  const canRegisterAnotherCategory = canRegisterInAnotherCategory({
    hasRegistration, registrationsOpen,
    challengeType: challengeLink?.challengeType,
    categoryCount: challengeLink?.registrationCategories?.length || 0,
  });
  const registrationActionLabel = canRegisterAnotherCategory
    ? "Inscrever em outra categoria"
    : hasRegistration
      ? "Continuar para pagamento"
    : !hasChallengeLink
      ? siteTexts.eventRegistration.buttons.unavailable
      : canRegisterNow
        ? "Quero me inscrever"
        : siteTexts.eventDetails.registrationClosedButton;
  const isFixedPairRegistration = (() => {
    if (String(challengeDetail?.challengeType || "").trim().toLowerCase() === "super8") {
      return false;
    }
    if (String(challengeDetail?.challengeType || "").trim().toLowerCase() === "poco") {
      return true;
    }
    if (challengeLink?.registrationType === "fixed_pair") return true;
    if (event?.registrationType === "fixed_pair") return true;
    return false;
  })();

  const amountPerPlayerCents = Number.isFinite(
    Number(challengeLink?.amountPerPlayerCents),
  )
    ? Number(challengeLink?.amountPerPlayerCents)
    : Number.isFinite(Number(event?.amountPerPlayerCents))
      ? Number(event?.amountPerPlayerCents)
      : null;
  const amountPerPlayerLabel = formatCurrency(amountPerPlayerCents);

  const participantsCount = Number(
    challengeDetail?.participantsCount || challengeDetail?.participantCpfs?.length || 0,
  );
  const slotsLimit = minimumParticipantsByType(challengeDetail);
  const pocoRegistrationCategories =
    String(challengeDetail?.challengeType || "").trim().toLowerCase() === "poco"
      ? (challengeLink?.registrationCategories?.length
          ? challengeLink.registrationCategories
          : challengeDetail?.registrationCategories || [])
      : [];
  const categoryRemainingPlayerSlots = getPocoTotalRemainingPlayers(
    pocoRegistrationCategories,
  );
  const availableSlots =
    pocoRegistrationCategories.length > 0
      ? categoryRemainingPlayerSlots
      : Math.max(0, slotsLimit - participantsCount);

  const challengeCategory = challengeCategorySummary(challengeDetail || undefined);
  const registeredEntries = [...(challengeDetail?.registrations || [])].sort(
    (a, b) => {
      const confirmedA = a.paymentStatus === "confirmed" ? 0 : 1;
      const confirmedB = b.paymentStatus === "confirmed" ? 0 : 1;
      if (confirmedA !== confirmedB) return confirmedA - confirmedB;

      const timeA = getRegistrationSortTime(getRegistrationOrderingDate(a));
      const timeB = getRegistrationSortTime(getRegistrationOrderingDate(b));
      if (timeA !== timeB) return timeA - timeB;

      return Number(a.id || 0) - Number(b.id || 0);
    },
  );
  const hasRegisteredEntries = registeredEntries.length > 0;

  const detailsText =
    String(event.details || "").trim() ||
    String(event.description || "").trim() ||
    "Venha participar do evento e garantir sua vaga com pagamento confirmado.";

  return (
    <AppLayout>
      <article className="event-v2-shell">
        <div className="event-v2-grid">
          <section className="event-v2-left">
            <div
              className="event-v2-poster"
              style={
                event.imageUrl
                  ? { backgroundImage: `url(${event.imageUrl})` }
                  : undefined
              }
            >
              <div className="event-v2-poster-overlay" />
              <div className="event-v2-poster-content">
                <p className="event-v2-brand">TNP EVENTO</p>
                <h2>{event.title}</h2>
                <div className="event-v2-tags">
                  <span className="event-v2-tag">
                    {isFixedPairRegistration ? "DUPLA FIXA" : "INDIVIDUAL"}
                  </span>
                  <span className="event-v2-tag">{challengeCategory}</span>
                  <span className="event-v2-tag">
                    {challengeTypeLabel(challengeDetail?.challengeType)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="event-v2-right">
            <div className="event-v2-panel event-v2-about">
              <div className="event-v2-about-head">
                <h3>Sobre o evento</h3>
                <div className="event-v2-status-row">
                  <span
                    className={`event-v2-status-pill event-v2-status-pill--${challengeStatusTone(
                      challengeDetail?.status || "pending",
                    )}`}
                  >
                    {challengeStatusLabel(challengeDetail?.status || "pending")}
                  </span>
                  {Boolean(challengeDetail?.includeInRanking) && (
                    <span
                      className="event-v2-ranking-badge"
                      title={siteTexts.challenges.publicList.labels.rankingIncluded}
                      aria-label={siteTexts.challenges.publicList.labels.rankingIncluded}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        emoji_events
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="event-v2-facts">
              <div className="event-v2-fact-item">
                <small>{siteTexts.eventDetails.date}</small>
                <strong>{event.date || siteTexts.eventDetails.fallbackDate}</strong>
              </div>
              <div className="event-v2-fact-item">
                <small>{siteTexts.eventDetails.address}</small>
                <EventLocationActions
                  address={event.address}
                  city={event.city}
                  state={event.state}
                  fallback={siteTexts.eventDetails.fallbackAddress}
                  valueAs="strong"
                  variant="fact"
                />
              </div>
              <div className="event-v2-fact-item">
                <small>{siteTexts.eventDetails.remainingSlots}</small>
                <strong>{availableSlots}</strong>
              </div>
              <div className="event-v2-fact-item">
                <small>{siteTexts.eventDetails.amountPerPlayer}</small>
                <strong>{amountPerPlayerLabel}</strong>
              </div>
            </div>

            <div className="event-v2-panel event-v2-details-panel">
              <h3>{siteTexts.eventDetails.details}</h3>
              <div className="event-v2-details-text">
                <p>{detailsText}</p>
              </div>
            </div>

            <div className="event-v2-panel event-v2-flow-note">
              <h4>{siteTexts.eventDetails.howRegistrationWorks}</h4>
              <p>
                {siteTexts.eventDetails.paymentGuarantee}
              </p>
            </div>

            {hasRegisteredEntries && (
              <div className="event-v2-panel event-v2-registrations-panel">
                <div className="event-v2-registrations-head">
                  <div>
                    <h3>{siteTexts.eventDetails.registrations.title}</h3>
                    <p>{siteTexts.eventDetails.registrations.sortedByPayment}</p>
                  </div>
                  <span>{registeredEntries.length}</span>
                </div>
                <ol className="event-v2-registrations-list">
                  {registeredEntries.map((registration, index) => {
                    const confirmationDate =
                      getRegistrationConfirmationDate(registration);
                    const updatedDate =
                      registration.updatedAt || registration.createdAt || null;
                    const confirmationLabel = confirmationDate
                      ? `${siteTexts.eventDetails.registrations.confirmedAtPrefix} ${formatDateTimeLabel(
                          confirmationDate,
                        )}`
                      : updatedDate
                        ? `${siteTexts.eventDetails.registrations.confirmedFallbackCreatedAtPrefix} ${formatDateTimeLabel(
                            updatedDate,
                          )}`
                        : siteTexts.eventDetails.registrations.confirmedNoDate;
                    const isConfirmed = registration.paymentStatus === "confirmed";

                    return (
                      <li
                        className="event-v2-registration-item"
                        key={registration.id}
                      >
                        <span className="event-v2-registration-position">
                          {index + 1}
                        </span>
                        <span className="event-v2-registration-main">
                          <strong>{registration.name}</strong>
                          {registration.registrationCategoryName ? (
                            <small>{registration.registrationCategoryName}</small>
                          ) : null}
                        </span>
                        <span
                          className={`event-v2-registration-confirmation${
                            isConfirmed
                              ? " event-v2-registration-confirmation--confirmed"
                              : ""
                          }`}
                          title={
                            isConfirmed
                              ? confirmationLabel
                              : paymentStatusLabel(registration.paymentStatus)
                          }
                          aria-label={
                            isConfirmed
                              ? confirmationLabel
                              : paymentStatusLabel(registration.paymentStatus)
                          }
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            {isConfirmed ? "flag" : "hourglass_top"}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            <div className="event-v2-actions">
              {canRegisterNow ? (
                <Button
                  variant="primary"
                  href={registrationHref}
                  className="event-v2-cta-btn"
                >
                  {registrationActionLabel}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  type="button"
                  className="event-v2-cta-btn"
                  disabled
                >
                  {registrationActionLabel}
                </Button>
              )}
            </div>

            <div className="event-v2-status-lines">
              {(myRegistrationLoading || challengeDetailLoading) && (
                <p className="status-message">
                  <Spinner label={siteTexts.eventDetails.loadingRegistrationStatus} />
                </p>
              )}
              {!myRegistrationLoading && myRegistration && (
                <p
                  className={`event-v2-registration-status event-v2-registration-status--${registrationStatusTone(
                    myRegistration.status,
                    myRegistration.paymentStatus,
                  )}`}
                >
                  <strong>{registrationStatusLabel(myRegistration.status)}</strong>
                  {myRegistration.paymentStatus ? (
                    <span>
                      {siteTexts.eventDetails.paymentStatus.prefix}{" "}
                      {paymentStatusLabel(myRegistration.paymentStatus)}
                    </span>
                  ) : null}
                </p>
              )}
              {challengeLinkLoading && (
                <p className="status-message">
                  <Spinner label={siteTexts.eventDetails.loadingEventInfo} />
                </p>
              )}
              {!challengeLinkLoading && !hasChallengeLink && (
                <p className="status-message">
                  {siteTexts.eventDetails.missingChallengeLink}
                </p>
              )}
              {!challengeLinkLoading &&
                hasChallengeLink &&
                !hasRegistration &&
                !registrationsOpen && (
                  <p className="status-message">
                    {siteTexts.eventDetails.registrationClosedMessage}
                  </p>
                )}
            </div>
          </section>
        </div>
      </article>
    </AppLayout>
  );
}
